import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { getTenantLogo } from '../../constants/Images';
import { Routes } from '../../routes';
import { useAppSelector } from '../../stores';
import { contributorDisplayName } from '../../utils/contributorDisplayName';
import SidebarUserProfileSection from '../../components/SidebarUserProfileSection';
import { GenericModal, RenameDataCollectionModal } from '../modal';
import { getTenantName } from '../../utils/brandingStyles';
import {
    canManageDataCollectionSummary,
    deleteCollectDataCollection,
    listRecentCollectCollections,
    patchCollectDataCollection,
    withdrawRecentCollectContribution,
    type RecentCollectCollectionSummary,
} from '../../services/api/collectDataService';

const BS_DROPDOWN_POPPER_FIXED = JSON.stringify({ strategy: 'fixed' });

function collectCollectionBadgeLabel(t: TFunction, status: string): string {
    const s = String(status).toLowerCase();
    if (s === 'draft') return t('form.collectWorkspace.collectionBadgeDraft', 'Draft');
    if (s === 'active') return t('form.collectWorkspace.collectionBadgeActive', 'Active');
    if (s === 'closed') return t('form.collectWorkspace.collectionBadgeClosed', 'Closed');
    return status ? String(status).replace(/_/g, ' ') : '';
}

export type ContributorParticipant = {
    id: string;
    name: string;
    initials: string;
    role: string;
    status: string;
    famla_fullname?: string;
    email?: string;
    isSelf?: boolean;
};

export interface RespondentSidebarProps {
    /** Current contributor — also listed under “Online now” when present */
    selfParticipant: ContributorParticipant | null;
    /** Other invited contributors */
    otherParticipants: ContributorParticipant[];
    /** From GET /r/:token — invited email matches a Famla user (logo → /home seulement avec session + ce flag) */
    inviteEmailHasFamlaAccount?: boolean;
    /** Même rangée « Ajouter un destinataire » que la vue owner (ouvre #inviteUser) */
    showAddRecipient?: boolean;
    /** Collecte affichée (pour fusion + surbrillance dans Recent, comme la vue workspace) */
    activeCollectionId?: string;
    activeCollection?: {
        name?: string | null;
        status?: string | null;
        created_at?: string | null;
        updated_at?: string | null;
    } | null;
    activeCollectionContributorToken?: string | null;
    activeCollectionOpenedAsContributor?: boolean;
    onActiveContributionWithdrawn?: () => void;
}

function statusLabel(t: TFunction, status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'completed') return t('words.completed', 'Completed');
    if (s === 'in_progress' || s === 'in progress') return t('form.collectWorkspace.inProgress', 'In progress');
    if (s === 'pending') return t('form.collectWorkspace.contributorStatusNotStarted', 'Not started');
    return s ? s.replace(/_/g, ' ') : t('words.pending', 'Pending');
}

function ParticipantRow({
    p,
    presence,
    t,
    compactFooter,
}: {
    p: ContributorParticipant;
    presence: 'online' | 'other';
    t: TFunction;
    /** Slightly denser row inside the pinned footer card */
    compactFooter?: boolean;
}) {
    const roleShown = (p.role || '').trim() || t('form.collectWorkspace.contributorDefaultRole', 'Contributor');
    const meta = `${roleShown} · ${statusLabel(t, p.status)}`;
    const rowClass =
        presence === 'online'
            ? 'collect-contributor-participant-row collect-contributor-participant-row--online'
            : 'collect-contributor-participant-row collect-contributor-participant-row--other';

    return (
        <div
            className={`role-person ${rowClass}${compactFooter ? ' collect-contributor-participant-row--footer' : ''}`}
            style={{ cursor: 'default' }}
        >
            <div className="role-person-left">
                <div className="collect-contributor-avatar-wrap">
                    <div className="mbadge collect-contributor-avatar-badge">{p.initials}</div>
                    <span
                        className="collect-contributor-avatar-presence"
                        data-presence={presence}
                        aria-hidden
                    />
                </div>
                <div className="role-person-info">
                    <span
                        className={
                            presence === 'other'
                                ? 'role-person-name collect-contributor-name--muted'
                                : 'role-person-name'
                        }
                    >
                        {contributorDisplayName(p)}
                    </span>
                    <div className="collect-contributor-role-status">{meta}</div>
                </div>
            </div>
        </div>
    );
}

function RespondentSidebar({
    selfParticipant,
    otherParticipants,
    inviteEmailHasFamlaAccount,
    showAddRecipient,
    activeCollectionId,
    activeCollection,
    activeCollectionContributorToken,
    activeCollectionOpenedAsContributor,
    onActiveContributionWithdrawn,
}: RespondentSidebarProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const auth = useAppSelector((state) => state.auth);
    const accessToken = auth.access_token;

    /** Même en-tête / profil bas que la vue owner collecte (session Famla + e-mail invité = compte Famla) */
    const useOwnerSidebarChrome = inviteEmailHasFamlaAccount === true && Boolean(accessToken);

    const [recentCollections, setRecentCollections] = useState<RecentCollectCollectionSummary[]>([]);
    const [recentLoading, setRecentLoading] = useState(false);
    /** Même logique que la vue owner : en-tête repliable + compteur (rôle / groupe). */
    const [othersCollapsed, setOthersCollapsed] = useState(true);
    const [recentRenameRow, setRecentRenameRow] = useState<RecentCollectCollectionSummary | null>(null);
    const [recentDeleteRow, setRecentDeleteRow] = useState<RecentCollectCollectionSummary | null>(null);
    const [recentWithdrawRow, setRecentWithdrawRow] = useState<RecentCollectCollectionSummary | null>(null);
    const [sidebarActionError, setSidebarActionError] = useState<string | null>(null);

    const loadRecent = useCallback(async () => {
        if (!accessToken) return;
        setRecentLoading(true);
        try {
            const r = await listRecentCollectCollections(accessToken, 25);
            if (r.status === 200 && Array.isArray(r.body?.data?.collections)) {
                setRecentCollections(r.body.data.collections as RecentCollectCollectionSummary[]);
            }
        } finally {
            setRecentLoading(false);
        }
    }, [accessToken]);

    useEffect(() => {
        if (!useOwnerSidebarChrome || !accessToken) return;
        void loadRecent();
    }, [useOwnerSidebarChrome, accessToken, loadRecent]);

    useEffect(() => {
        setOthersCollapsed(true);
    }, [activeCollectionId]);

    const showOnline = Boolean(selfParticipant);
    const showOthers = otherParticipants.length > 0;

    const recentCollectionsVisible = useMemo(() => {
        const rows = Array.isArray(recentCollections) ? [...recentCollections] : [];
        if (!activeCollectionId) return rows;
        const currentRow: RecentCollectCollectionSummary = {
            id: activeCollectionId,
            name:
                String(activeCollection?.name ?? '').trim() ||
                t('form.collectWorkspace.collectionNameFallback', { defaultValue: 'Untitled collection' }),
            status: String(activeCollection?.status ?? 'draft'),
            created_at: activeCollection?.created_at ?? undefined,
            updated_at: activeCollection?.updated_at ?? activeCollection?.created_at ?? new Date().toISOString(),
        };
        const idx = rows.findIndex((r) => r.id === activeCollectionId);
        const contributorFlags = {
            opened_as_contributor: activeCollectionOpenedAsContributor ?? undefined,
            contributor_invitation_token: activeCollectionContributorToken?.trim() || undefined,
        };
        if (idx >= 0) rows[idx] = { ...rows[idx], ...currentRow, ...contributorFlags };
        else rows.unshift({ ...currentRow, ...contributorFlags });
        rows.sort((a, b) => {
            const ta = new Date((a.updated_at ?? a.created_at ?? '') as string).getTime() || 0;
            const tb = new Date((b.updated_at ?? b.created_at ?? '') as string).getTime() || 0;
            return tb - ta;
        });
        return rows;
    }, [
        recentCollections,
        activeCollectionId,
        activeCollection,
        activeCollectionContributorToken,
        activeCollectionOpenedAsContributor,
        t,
    ]);

    const goToRecentCollection = useCallback(
        (row: RecentCollectCollectionSummary) => {
            if (activeCollectionId && row.id === activeCollectionId) return;
            const inviteTok = row.contributor_invitation_token?.trim();
            if (row.opened_as_contributor && inviteTok) {
                navigate(`/r/${inviteTok}`);
                return;
            }
            navigate(`/process/data-collect/${row.id}`);
        },
        [activeCollectionId, navigate],
    );

    const handleRecentRename = useCallback(
        (row: RecentCollectCollectionSummary) => {
            if (!accessToken || !canManageDataCollectionSummary(row)) return;
            setSidebarActionError(null);
            setRecentRenameRow(row);
        },
        [accessToken],
    );

    const executeRecentRename = useCallback(
        async (trimmed: string): Promise<boolean> => {
            if (!accessToken || !recentRenameRow) return false;
            const res = await patchCollectDataCollection(recentRenameRow.id, { name: trimmed }, accessToken);
            if (res.status !== 200) {
                setSidebarActionError(res.body?.description || t('words.error'));
                return false;
            }
            setSidebarActionError(null);
            await loadRecent();
            return true;
        },
        [accessToken, loadRecent, recentRenameRow, t],
    );

    const executeRecentDelete = useCallback(async (): Promise<boolean> => {
        if (!accessToken || !recentDeleteRow || !canManageDataCollectionSummary(recentDeleteRow)) return false;
        const res = await deleteCollectDataCollection(recentDeleteRow.id, accessToken);
        if (res.status !== 200) {
            setSidebarActionError(res.body?.description || t('words.error'));
            return false;
        }
        const deletedId = recentDeleteRow.id;
        setSidebarActionError(null);
        await loadRecent();
        if (activeCollectionId && deletedId === activeCollectionId) {
            navigate(Routes.DATA_COLLECT);
        }
        return true;
    }, [accessToken, activeCollectionId, loadRecent, navigate, recentDeleteRow, t]);

    const executeRecentWithdraw = useCallback(async (): Promise<boolean> => {
        if (!recentWithdrawRow) return false;
        const fallbackToken =
            activeCollectionId && recentWithdrawRow.id === activeCollectionId
                ? activeCollectionContributorToken
                : undefined;
        const { ok } = await withdrawRecentCollectContribution(recentWithdrawRow, accessToken, { fallbackToken });
        if (!ok) {
            setSidebarActionError(t('words.error'));
            return false;
        }
        setSidebarActionError(null);
        if (activeCollectionId && recentWithdrawRow.id === activeCollectionId) {
            onActiveContributionWithdrawn?.();
            return true;
        }
        await loadRecent();
        return true;
    }, [
        accessToken,
        activeCollectionContributorToken,
        activeCollectionId,
        loadRecent,
        onActiveContributionWithdrawn,
        recentWithdrawRow,
        t,
    ]);

    const recentRowsNodes = recentCollectionsVisible.map((row) => {
        const active = Boolean(activeCollectionId && row.id === activeCollectionId);
        const canManage = canManageDataCollectionSummary(row);
        const showOwnerRowMenu = canManage;
        const showContributorRowMenu = Boolean(row.opened_as_contributor) && !canManage;
        const showRowMenu = showOwnerRowMenu || showContributorRowMenu;
        return (
            <div key={row.id} className={`collect-ws-recent-item ${active ? 'active' : ''}`}>
                <button type="button" className="collect-ws-recent-item__nav" onClick={() => goToRecentCollection(row)}>
                    <span className="collect-ws-recent-item__name" title={row.name}>
                        {row.name}
                    </span>
                    <span className="collect-ws-recent-item__meta">{collectCollectionBadgeLabel(t, row.status)}</span>
                </button>
                {showRowMenu ? (
                    <div
                        className="collect-ws-recipient-menu"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        <div className="dropdown">
                            <button
                                type="button"
                                className="btn btn-link text-secondary text-decoration-none p-1 collect-ws-recipient-menu__toggle"
                                data-bs-toggle="dropdown"
                                data-bs-popper-config={BS_DROPDOWN_POPPER_FIXED}
                                aria-expanded="false"
                                aria-label={t(
                                    'form.collectWorkspace.recentCollectionRowMenu',
                                    'Collection actions',
                                )}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <i className="fa fa-ellipsis-vertical small" aria-hidden />
                            </button>
                            <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-end small border border-secondary shadow-sm">
                                {showOwnerRowMenu ? (
                                    <li>
                                        <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() => handleRecentRename(row)}
                                        >
                                            {t('words.rename')}
                                        </button>
                                    </li>
                                ) : null}
                                {showOwnerRowMenu ? (
                                    <li>
                                        <button
                                            type="button"
                                            className="dropdown-item text-danger"
                                            onClick={() => {
                                                setSidebarActionError(null);
                                                setRecentDeleteRow(row);
                                            }}
                                        >
                                            {t('words.delete')}
                                        </button>
                                    </li>
                                ) : null}
                                {showContributorRowMenu ? (
                                    <li>
                                        <button
                                            type="button"
                                            className="dropdown-item text-danger"
                                            onClick={() => {
                                                setSidebarActionError(null);
                                                setRecentWithdrawRow(row);
                                            }}
                                        >
                                            {t('form.collectWorkspace.withdrawMyContribution', {
                                                defaultValue: 'Remove my contribution',
                                            })}
                                        </button>
                                    </li>
                                ) : null}
                            </ul>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    });

    const othersToggleHeader = showOthers ? (
        <div
            className={`role-header ${!othersCollapsed ? 'active' : ''}`}
            role="button"
            tabIndex={0}
            aria-expanded={!othersCollapsed}
            aria-controls="contributor-others-members"
            id="contributor-others-header"
            onClick={() => setOthersCollapsed((c) => !c)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOthersCollapsed((c) => !c);
                }
            }}
        >
            <div className="role-dot" />
            <span className="role-name">{t('form.collectWorkspace.contributorOthers', 'Others')}</span>
            <div className="role-header-end">
                <span className={`role-arrow ${!othersCollapsed ? 'open' : ''}`} aria-hidden>
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                        <path
                            d="M1 1L5 5L9 1"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
                <span className="role-count">{otherParticipants.length}</span>
            </div>
        </div>
    ) : null;

    const othersTopBlock =
        showOthers ? (
            <div className="role-block" style={{ marginTop: showOnline ? 18 : 0 }}>
                {othersToggleHeader}
            </div>
        ) : null;

    const othersMembersList = !othersCollapsed ? (
        <div
            id="contributor-others-members"
            role="region"
            aria-labelledby="contributor-others-header"
            className="role-members collect-contributor-subgroup-list"
            style={{
                borderLeft: '1px solid rgba(255,255,255,0.05)',
                marginLeft: 6,
                paddingLeft: 8,
            }}
        >
            {otherParticipants.map((p) => (
                <ParticipantRow key={p.id} p={p} presence="other" t={t} />
            ))}
        </div>
    ) : null;

    return (
        <div className={`left-panel collect-contributor-left${useOwnerSidebarChrome ? ' collect-contributor-left--owner-chrome' : ''}`}>
            {useOwnerSidebarChrome ? (
                <div className="lp-header">
                    <button type="button" className="back-btn" onClick={() => navigate(Routes.HOMEVIEW)}>
                        <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                            <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {t('words.home')}
                    </button>
                </div>
            ) : (
                <div className="lp-header collect-contributor-lp-header-brand" role="img" aria-label={getTenantName()}>
                    <img src={getTenantLogo('dark')} alt="" className="collect-contributor-lp-logo" aria-hidden />
                </div>
            )}

            {useOwnerSidebarChrome ? (
                <div className="lp-section-scrollable collect-contributor-scroll-shell--split">
                    <div className="collect-contributor-scroll-split-top">
                        <div className="lp-section collect-contributor-participants-section">
                            <div className="lp-label">{t('form.collectWorkspace.recipientsTitle', 'Recipients')}</div>

                            {showOnline ? (
                                <>
                                    <div className="collect-contributor-subgroup-label">
                                        {t('form.collectWorkspace.contributorOnlineNow', 'Online now')}
                                    </div>
                                    <div className="collect-contributor-subgroup-list">
                                        {selfParticipant ? <ParticipantRow p={selfParticipant} presence="online" t={t} /> : null}
                                    </div>
                                </>
                            ) : null}

                            {othersTopBlock}

                            {!showOthers && !showOnline ? (
                                <p className="collect-muted collect-contributor-empty-hint">
                                    {t('form.collectWorkspace.contributorOnlyYouInList', 'No other participants yet.')}
                                </p>
                            ) : null}

                            {showAddRecipient ? (
                                <div
                                    className="add-btn2"
                                    role="button"
                                    tabIndex={0}
                                    data-bs-toggle="modal"
                                    data-bs-target="#inviteUser"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
                                    }}
                                >
                                    <svg style={{ width: 11, height: 11 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                        <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                    </svg>
                                    {t('form.collectWorkspace.addRecipient', 'Add recipient')}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="collect-contributor-split-main">
                        {showOthers && !othersCollapsed ? (
                            <div className="collect-contributor-others-split-column">
                                <div className="collect-contributor-others-scroll-body">{othersMembersList}</div>
                            </div>
                        ) : null}

                        <div className="collect-contributor-recent-split-column lp-section collect-ws-recent-section">
                            <div className="lp-label">{t('form.collectWorkspace.recentDataCollections', 'Recent')}</div>
                            {sidebarActionError ? (
                                <p className="collect-alert danger small mb-2" role="alert" style={{ fontSize: '0.78rem' }}>
                                    {sidebarActionError}
                                </p>
                            ) : null}
                            <div className="collect-contributor-recent-scroll-head">
                                {recentLoading && recentCollectionsVisible.length === 0 ? (
                                    <p className="collect-muted" style={{ fontSize: '0.78rem' }}>
                                        {t('words.loading')}
                                    </p>
                                ) : null}
                                {!recentLoading && recentCollectionsVisible.length === 0 ? (
                                    <p className="collect-muted" style={{ fontSize: '0.78rem' }}>
                                        {t('form.collectWorkspace.recentDataCollectionsEmpty', 'No collections yet.')}
                                    </p>
                                ) : null}
                            </div>
                            <div className="collect-contributor-recent-scroll-body">{recentRowsNodes}</div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="lp-section-scrollable">
                    <div className="lp-section collect-contributor-participants-section">
                        <div className="lp-label">{t('form.collectWorkspace.recipientsTitle', 'Recipients')}</div>

                        {showOnline ? (
                            <>
                                <div className="collect-contributor-subgroup-label">
                                    {t('form.collectWorkspace.contributorOnlineNow', 'Online now')}
                                </div>
                                <div className="collect-contributor-subgroup-list">
                                    {selfParticipant ? <ParticipantRow p={selfParticipant} presence="online" t={t} /> : null}
                                </div>
                            </>
                        ) : null}

                        {showOthers ? (
                            <div className="role-block" style={{ marginTop: showOnline ? 18 : 0 }}>
                                {othersToggleHeader}
                                {othersMembersList}
                            </div>
                        ) : !showOnline ? (
                            <p className="collect-muted collect-contributor-empty-hint">
                                {t('form.collectWorkspace.contributorOnlyYouInList', 'No other participants yet.')}
                            </p>
                        ) : null}

                        {showAddRecipient ? (
                            <div
                                className="add-btn2"
                                role="button"
                                tabIndex={0}
                                data-bs-toggle="modal"
                                data-bs-target="#inviteUser"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
                                }}
                            >
                                <svg style={{ width: 11, height: 11 }} viewBox="0 0 16 16" fill="none" aria-hidden>
                                    <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                </svg>
                                {t('form.collectWorkspace.addRecipient', 'Add recipient')}
                            </div>
                        ) : null}
                    </div>
                </div>
            )}

            {useOwnerSidebarChrome ? (
                <SidebarUserProfileSection />
            ) : selfParticipant ? (
                <div className="collect-contributor-left-footer">
                    <div className="collect-contributor-footer-card">
                        <ParticipantRow p={selfParticipant} presence="online" t={t} compactFooter />
                    </div>
                </div>
            ) : null}

            {recentRenameRow ? (
                <RenameDataCollectionModal
                    collectionId={recentRenameRow.id}
                    initialName={recentRenameRow.name}
                    modalDomId={`famla-dc-rename-sidebar-${recentRenameRow.id}`}
                    autoShow
                    onHidden={() => setRecentRenameRow(null)}
                    onSave={executeRecentRename}
                />
            ) : null}

            {recentDeleteRow ? (
                <GenericModal
                    title={t('words.delete')}
                    actionButton={t('words.delete')}
                    sentence={
                        String(recentDeleteRow.status).toLowerCase() === 'draft'
                            ? t('form.collectWorkspace.deleteDataCollectionConfirm', {
                                  defaultValue: 'Delete this draft?',
                              })
                            : t('form.collectWorkspace.deleteDataCollectionConfirmActive', {
                                  defaultValue:
                                      'Delete this collection and all responses? This cannot be undone.',
                              })
                    }
                    processId={recentDeleteRow.id}
                    modalDomId={`famla-dc-delete-sidebar-${recentDeleteRow.id}`}
                    autoShow
                    onHidden={() => setRecentDeleteRow(null)}
                    action={() => executeRecentDelete()}
                />
            ) : null}

            {recentWithdrawRow ? (
                <GenericModal
                    title={t('form.collectWorkspace.withdrawMyContribution', {
                        defaultValue: 'Remove my contribution',
                    })}
                    actionButton={t('form.collectWorkspace.withdrawMyContribution', {
                        defaultValue: 'Remove my contribution',
                    })}
                    sentence={t('form.collectWorkspace.withdrawMyContributionConfirm', {
                        defaultValue:
                            'Remove your contribution from this collection? Your responses and messages will be deleted.',
                    })}
                    processId={recentWithdrawRow.id}
                    modalDomId={`famla-dc-withdraw-sidebar-${recentWithdrawRow.id}`}
                    autoShow
                    onHidden={() => setRecentWithdrawRow(null)}
                    action={() => executeRecentWithdraw()}
                />
            ) : null}
        </div>
    );
}

export default RespondentSidebar;
